import { useState } from "react";
import { AxiosResponse, AxiosError } from "axios";
import fleetlogicAPI from "helpers/fleetlogicAPI";
import { StatusCodes } from "http-status-codes";
import { AuthorizationToken } from "helpers/AuthorizationToken";
import { isValidNumber, SrmBoolean, getApiErrorMsg } from "helpers/helper";
import reduxStore from "reduxstore/reduxStore";
import {
  setCurrentUser,
  generateOldPermissionData,
  generateNewPermissionData
} from "actions/authActions";
import {
  APP_BASE_PATH,
  AUTH_URL,
  COMPANY_PERMISSION_URL,
  USER_URL,
  USER_PERMISSION_URL,
  USER_INSTANCE_URL,
  OPERATOR_URL,
  COMPANY_UPGRADED_ERROR,
  TIS_SESSION_VERIFY_URL,
  TIS_REGISTER_URL,
  NON_UPGRADED_USER_URL,
  INTERNAL_EMAIL_ACCOUNT,
  SCS_SRM_UID
} from "constants/AppConstants";
import i18n from "i18n/i18n";
import { get, isEmpty, pick } from "lodash";
import { Alert } from "@texada/ui";
import { setCompressedItem } from "helpers/compressedStorage";

enum APPLICATION_ID {
  SRM = 100026,
  TW = 100027,
  GW = 100029
}

export enum LoginSource {
  SRM = "SRM",
  TW = "TW",
  TM = "TM",
  BOTH = "both"
}

export enum LoginTenant {
  LIVE = "Live",
  PLAY = "Play"
}

export interface LoginData {
  email: string;
  password: string;
  company_id?: number;
  loginSource?: string;
}

interface MobileLoginData {
  mobile_session_token: string;
}

interface LoginResponse {
  token: string;
  flask_token: string;
  flask_url: string;
  gateway_token: string;
  gateway_url: string;
}

export interface LoginFullResponse extends LoginResponse {
  user: any;
  is_operator?: boolean;
  use_texada_pay?: boolean;
  comp_perms: any;
  permissions: any;
  locations: any[];
  divisions: any[];
  login_divisions?: any[];
  system_info: any;
  upgraded?: boolean;
  companyId?: number;
  isSuperAdmin?: boolean;
  company_sso_enabled?: boolean;
  company_sso_config?: any;
  company_features?: any;
  gateway_features?: any;
}

interface SetTokenParam {
  token: string;
  flask_token: string;
  flask_url: string;
  authRefreshToken?: string;
  gateway_token: string;
  gateway_url: string;
}

function isMobileLoginData(
  options: LoginData | MobileLoginData
): options is MobileLoginData {
  return options.hasOwnProperty("mobile_session_token");
}

function isFullResponse(
  response: AxiosResponse<LoginResponse | LoginFullResponse>
): response is AxiosResponse<LoginFullResponse> {
  return response.status === StatusCodes.OK;
}

type UserResponse = Omit<LoginFullResponse, keyof LoginResponse>;

export async function setTokens({
  token,
  flask_token,
  flask_url,
  gateway_token,
  gateway_url
}: SetTokenParam) {
  localStorage.setItem("flaskToken", flask_token);
  localStorage.setItem("flask_url", flask_url);
  if (gateway_token) {
    localStorage.setItem("gatewayToken", gateway_token);
    localStorage.setItem("gateway_url", gateway_url);
  } else {
    localStorage.removeItem("gatewayToken");
    localStorage.removeItem("gateway_url");
  }
  if (token) {
    localStorage.setItem("jwtToken", token);
  }
}

export async function setUser(loginResponse: LoginFullResponse) {
  const user = {
    ...loginResponse.user,
    compperms: loginResponse.comp_perms,
    systemInfo: loginResponse.system_info,
    companyFeatures: loginResponse.company_features,
    gatewayFeatures: loginResponse.gateway_features,
    locations: loginResponse.locations,
    divisions: loginResponse.divisions,
    login_divisions: loginResponse?.login_divisions || [],
    useTexadaPay: loginResponse.use_texada_pay,
    upgraded: loginResponse.upgraded,
    companyId: loginResponse.companyId,
    permissions: loginResponse.permissions,
    company_sso_enabled:
      loginResponse.company_sso_enabled ||
      loginResponse.user.company_sso_enabled,
    isSuperAdmin: false,
    companySsoConfig: loginResponse.company_sso_config
  };

  if (loginResponse.isSuperAdmin) {
    user.isSuperAdmin = loginResponse.isSuperAdmin;
  }
  localStorage.setItem("isOperator", JSON.stringify(loginResponse.is_operator));
  setCompressedItem("currentUser", JSON.stringify(user));
  await reduxStore.dispatch(setCurrentUser(user));
  return user;
}

function pollQueueAPI(url): Promise<AxiosResponse<UserResponse>> {
  const timeInterval = 1000;
  const timeout = 3 * 60 * 1000;
  const maxIterations = timeout / timeInterval;
  let iteration = 0;

  async function makeRequest(resolve, reject) {
    const response: AxiosResponse<never | UserResponse> = await fleetlogicAPI
      .get(url)
      .catch(error => {
        if (!error.hasOwnProperty("response"))
          reject(
            i18n.t(
              "connection_error_please_try_again_later_or_contact_texada_software"
            )
          );
        return error.response;
      });

    if (!response.request.responseURL.includes(url)) {
      resolve(response);
    } else if (response.status === 200) {
      const shouldTimeout = iteration++ >= maxIterations;
      if (shouldTimeout)
        reject(
          i18n.t(
            "connection_error_please_try_again_later_or_contact_texada_software"
          )
        );
      else setTimeout(() => makeRequest(resolve, reject), timeInterval);
    } else {
      reject(response);
    }
  }

  return new Promise(makeRequest);
}

export type LoginFunc = (
  postData: LoginData | MobileLoginData
) => Promise<void>;

export function useLogin(
  setIsSyncingData: React.Dispatch<React.SetStateAction<boolean>>
) {
  const [error, setError] = useState<AxiosError>();

  const login: LoginFunc = async ({
    ...postData
  }: LoginData | MobileLoginData) => {
    setError(undefined);
    if (!isMobileLoginData(postData)) {
      postData.email = postData.email.trim();
    }

    try {
      const response = await fleetlogicAPI.post<
        LoginResponse | LoginFullResponse
      >(AUTH_URL, postData);
      if (response?.data?.message === COMPANY_UPGRADED_ERROR) {
        window.location.href = `${APP_BASE_PATH}/company/${response?.data?.company}`;
        return false;
      }
      await setTokens(response.data);
      AuthorizationToken.set(response.data.token);

      if (isFullResponse(response)) {
        if (response.data.upgraded) {
          await handleNewLoginResponse(response);
        } else {
          await handleOldLoginResponse(response);
        }
      } else {
        setIsSyncingData(true);
        const { location: queueUrl } = response.headers;
        const userResponse = await pollQueueAPI(queueUrl);
        if (userResponse.data.upgraded) {
          await handleNewLoginResponse(userResponse);
        } else {
          await handleOldLoginResponse(userResponse);
        }
      }
    } catch (error) {
      setIsSyncingData(false);
      setError(error);
      throw error;
    }
  };

  const loginAuth = async (
    tenantId,
    showDivisionModal = undefined,
    tisSession = undefined,
    CompanySsoEnabled = false,
    email,
    token = undefined
  ) => {
    setError(undefined);

    try {
      const res = await fleetlogicAPI.post(
        `${TIS_SESSION_VERIFY_URL}/${tenantId}`,
        {
          tis_session: tisSession,
          company_sso_enabled: CompanySsoEnabled,
          email,
          source: LoginSource.TW
        },
        {
          headers: {
            Authorization: `JWT ${token}`
          }
        }
      );
      await setTokens(res.data);
      AuthorizationToken.set(res.data.token);
      if (res.data.company.upgraded) {
        return await handleNewLoginResponse(
          res,
          showDivisionModal,
          CompanySsoEnabled
        );
      } else {
        localStorage.setItem("currentTenant", tenantId);
        return await handleOldLoginResponse(res, showDivisionModal);
      }
    } catch (error) {
      if (!token) {
        setError(error);
      }
      throw error;
    }
  };

  return { login, loginAuth, error, setError };
}

const normalizeAppRedirectUrl = (url: string) => {
  const redirectUrl = new URL(url, window.location.origin);

  if (
    APP_BASE_PATH &&
    redirectUrl.origin === window.location.origin &&
    redirectUrl.pathname !== APP_BASE_PATH &&
    !redirectUrl.pathname.startsWith(`${APP_BASE_PATH}/`)
  ) {
    redirectUrl.pathname = `${APP_BASE_PATH}${redirectUrl.pathname}`;
  }

  return redirectUrl.toString();
};

export async function openSrmManagement(
  upgraded = false,
  divisionSrmUid = undefined,
  divLocId = undefined
) {
  let url = "/rest-api/srmlogin";
  if (divLocId) {
    url = `/rest-api/srmlogin?div_loc_id=${encodeURIComponent(divLocId)}`;
  } else if (upgraded && divisionSrmUid !== undefined) {
    url = `/rest-api/srmlogin?division=${encodeURIComponent(divisionSrmUid)}`;
  }
  try {
    const loginURL = await fleetlogicAPI.get(url);
    window.location.href = normalizeAppRedirectUrl(loginURL.data);
  } catch {
    Alert.error({
      message: i18n.t("could_not_login_to_srf_classic")
    });
  }
}

export const handleOldLoginResponse = async (
  response,
  showDivisionModal = undefined
) => {
  const { user, comp_perms } = response.data;
  const permissions = generateOldPermissionData(
    comp_perms,
    response.data.divisions,
    response.data.locations
  );

  if (showDivisionModal && comp_perms.operator_by_division) {
    showDivisionModal(true);
  }

  let userInfo = {
    ...response.data,
    isSuperAdmin: response.data.user.is_super_admin,
    srm_uid: response.data.user.srm_uid,
    permissions: {
      ...permissions.permissions,
      admin: user.settings_menu_access,
      update_settings: user.settings_menu_access,
      capture_signatures: user.capture_signatures,
      change_asset_status: user.change_asset_status,
      maintain_sites: user.maintain_sites,
      update_meter_reading: user.update_meter_reading,
      view_inventory: user.view_inventory,
      workorder_default_reporting: user.workorder_default_reporting
    },
    locations: permissions.locations,
    divisions: permissions.divisions,
    login_divisions: response.data.login_divisions,
    companyId: response.data.user.company,
    tenant_id: response.data.user.tenant_id
  };

  const userInfoWithDocAccess = await setDocDivisionAccess(userInfo);
  await setUser(userInfoWithDocAccess);

  if (showDivisionModal && !comp_perms.operator_by_division) {
    return true;
  }
  return false;
};

export const handleNewLoginResponse = async (
  response,
  showDivisionModal = undefined
) => {
  const companyPermissionRes = await fleetlogicAPI.get(
    `${COMPANY_PERMISSION_URL}${response.data.user.company}`
  );
  const userRes = await fleetlogicAPI.get(
    `${USER_URL}${response.data.user.id}?include_flask=true`
  );
  const permissionRes = await fleetlogicAPI.get(
    `${USER_PERMISSION_URL}${response.data.user.id}`
  );
  const permissions = generateNewPermissionData(
    companyPermissionRes.data,
    permissionRes.data.divisions,
    permissionRes.data.locations
  );

  if (showDivisionModal && companyPermissionRes.data.operator_by_division) {
    showDivisionModal(true);
  }

  let userInfo = {
    ...response.data,
    user: userRes.data,
    srm_uid: userRes.data.srm_uid,
    // TODO: remove compperms
    comp_perms: companyPermissionRes.data,
    permissions: permissions.permissions,
    locations: permissions.locations,
    divisions: permissions.divisions,
    useTexadaPay: companyPermissionRes.data.use_texada_pay,
    companyId: response.data.user.company,
    upgraded: companyPermissionRes.data.upgraded,
    isSuperAdmin: permissionRes.data.is_super_admin,
    is_operator: userRes.data.is_support
  };

  const userInfoWithDocAccess = await setDocDivisionAccess(userInfo);
  await setUser(userInfoWithDocAccess);

  if (showDivisionModal && !companyPermissionRes.data.operator_by_division) {
    return true;
  }
  return false;
};

export function getLoginError(error: AxiosError): string {
  if (!error || isSCSGeneratePassword(error)) {
    return "";
  }

  if (error?.response?.data?.message) {
    if (get(error, "response.status_code", "") === StatusCodes.NOT_ACCEPTABLE) {
      return i18n.t(
        "there_is_a_new_app_version_available_please_refresh_page_to_continue"
      );
    }
    return error.response.data.message;
  }

  if (error?.response?.data?.form) {
    return error.response.data.form;
  }

  if (error?.message && !error?.response) {
    return error.message;
  }

  return (
    getApiErrorMsg(error) ||
    i18n.t("something_went_wrong_please_contact_texada_software")
  );
}

export async function switchInstance(userId, currentTenantId, tenantId) {
  if (!currentTenantId || !tenantId || currentTenantId === tenantId)
    return false;

  if (!isValidNumber(tenantId) || !isValidNumber(currentTenantId))
    return false;

  try {
    const res = await fleetlogicAPI.patch(`${USER_INSTANCE_URL}/${userId}`, {
      tenant_id: tenantId
    });

    if (!res?.data) {
      return false;
    }
    return true;
  } catch (err) {
    console.log(err);
  }

  return false;
}

export function isSCSGeneratePassword(error: AxiosError): boolean {
  return (
    error?.response?.status === StatusCodes.FORBIDDEN &&
    error?.response?.data?.message
  );
}

export async function setDocDivisionAccess(user, divisionSrmUid = "", divLocId = "") {
  try {
    let url = `${OPERATOR_URL}/${user.srm_uid}/${encodeURIComponent(divisionSrmUid)}`;
    if (divLocId) {
      url += `?div_loc_id=${encodeURIComponent(divLocId)}`;
    }
    const res = await fleetlogicAPI.get(url, { responseType: "json" });

    let resArray =
      res.data?.operators?.[0]?.divisions?.length > 0
        ? res.data.operators[0].divisions
        : [];
    if (!(user?.compperms || user?.comp_perms)?.operator_by_division) {
      const found = resArray.find(
        division => division.division.trim() === divisionSrmUid.trim()
      );
      if (found) {
        resArray = [found];
      }
    }
    // Deep clone divisions and locations
    let newDivisions = user.divisions.map(d => ({ ...d }));
    let newLocations = user.locations.map(l => ({ ...l }));

    const docDivisionAccess = [];
    const docLocationAccess = [];

    resArray.forEach(division => {
      // Handle divisions
      const userDivisionIndex = newDivisions.findIndex(
        e => e.warehouse.srm_uid === division.division.trim()
      );
      if (userDivisionIndex > -1) {
        const oldDivision = newDivisions[userDivisionIndex];
        const updatedDivision = {
          ...oldDivision,
          prompt_division:
            division.promptForDiv === SrmBoolean.True ||
            oldDivision.prompt_division,
          is_doc_division: true
        };
        newDivisions[userDivisionIndex] = updatedDivision;

        const docDivisionIndex = docDivisionAccess.findIndex(
          e => e.srm_uid === updatedDivision.warehouse.srm_uid
        );
        if (docDivisionIndex > -1) {
          const docDivision = docDivisionAccess[docDivisionIndex];
          docDivisionAccess[docDivisionIndex] = {
            ...docDivision,
            prompt_division:
              updatedDivision.prompt_division || docDivision.prompt_division,
            is_doc_division:
              updatedDivision.is_doc_division || docDivision.is_doc_division
          };
        } else {
          docDivisionAccess.push({
            srm_uid: updatedDivision.warehouse.srm_uid,
            ...pick(updatedDivision, ["prompt_division", "is_doc_division"])
          });
        }
      }

      // Handle locations
      const userLocationIndex = newLocations.findIndex(
        e => e.warehouse.srm_uid === division.location.trim()
      );
      if (userLocationIndex > -1) {
        const oldLocation = newLocations[userLocationIndex];
        const updatedLocation = {
          ...oldLocation,
          prompt_location:
            division.promptForLoc === SrmBoolean.Division
              ? division.promptForLoc
              : division.promptForLoc === SrmBoolean.True,
          is_doc_location: true
        };
        newLocations[userLocationIndex] = updatedLocation;

        const docLocationIndex = docLocationAccess.findIndex(
          e => e.srm_uid === updatedLocation.warehouse.srm_uid
        );
        if (docLocationIndex > -1) {
          const docLocation = docLocationAccess[docLocationIndex];
          docLocationAccess[docLocationIndex] = {
            ...docLocation,
            prompt_location:
              updatedLocation.prompt_location || docLocation.prompt_location,
            is_doc_location:
              updatedLocation.is_doc_location || docLocation.is_doc_location
          };
        } else {
          docLocationAccess.push({
            srm_uid: updatedLocation.warehouse.srm_uid,
            ...pick(updatedLocation, ["prompt_location", "is_doc_location"])
          });
        }
      }
    });

    const preSelected =
      JSON.parse(localStorage.getItem("docDivisionAccess")) || {};
    localStorage.setItem(
      "docDivisionAccess",
      JSON.stringify({
        ...preSelected,
        [user.tenant_id]: {
          divisions: docDivisionAccess,
          locations: docLocationAccess
        }
      })
    );

    return {
      ...user,
      divisions: newDivisions,
      locations: newLocations
    };
  } catch (err) {
    console.debug(err);
    return user;
  }
}

export function getLoginSource(source, selected) {
  if (source === "mobile") {
    return LoginSource.TM;
  }

  return selected ? selected : LoginSource.TW;
}

export const getSelectedLoginTo = applicationId => {
  if (Number(applicationId) === APPLICATION_ID.SRM) return LoginSource.SRM;
  return LoginSource.TW;
};

export const getUserStatus = async email => {
  try {
    const res = await fleetlogicAPI.post(
      `${NON_UPGRADED_USER_URL}/migrate-status`,
      {
        email
      }
    );
    return res.data;
  } catch (error) {
    console.log("Error checking email verification status:", error);
    return false;
  }
};

export const getTisRegistrationUrl = async (
  userInfo,
  emailVerified = undefined,
  isLogin = false
) => {
  if (!userInfo) return null;

  const permission = userInfo?.companyFeatures?.tis_mandatory_migration;
  const tisMigrated = !isEmpty(userInfo?.tis_user_id);
  const isEmailVerified = emailVerified ?? userInfo?.email_verified;
  const ssoEnabled = userInfo?.company_sso_enabled;
  const isForceMigration =
    new Date() >=
    new Date(userInfo?.companyFeatures?.tis_mandatory_migration_cut_off_date);
  if (
    userInfo?.email?.toLowerCase()?.includes(INTERNAL_EMAIL_ACCOUNT) ||
    userInfo?.srm_uid === SCS_SRM_UID
  )
    return null;
  if (!permission || !ssoEnabled) return null;
  if (isEmailVerified === false && tisMigrated) return `/${TIS_REGISTER_URL}`;
  if (!isLogin && !isForceMigration) return null;
  if (!tisMigrated) return `/${TIS_REGISTER_URL}`;
  return null;
};
